import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiConfig';

function DocumentViewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cedula } = useParams();

  // Estados del documento
  const [documentUrl, setDocumentUrl] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [documentoDB, setDocumentoDB] = useState(null);
  const [documentId, setDocumentId] = useState(null);

  // Estados de carga y errores
  const [cargandoDocumento, setCargandoDocumento] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [reintentos, setReintentos] = useState(0);
  const maxReintentos = 3;

  // Estados de procesamiento
  const [procesandoRechazo, setProcesandoRechazo] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);

  // Estados de previsualización
  const [visorCargado, setVisorCargado] = useState(false);
  const [tipoVisor, setTipoVisor] = useState('google'); // 'google', 'mozilla', 'directo'

  // ⭐ NUEVO: Obtener índice de firmante (para documentos administrativos multi-firmante)
  const firmanteIndex = location.state?.firmanteIndex;

  // Obtener documentId desde state o buscar documento pendiente
  useEffect(() => {
    const docIdFromState = location.state?.documentoId;
    if (docIdFromState) {
      setDocumentId(docIdFromState);
    } else if (cedula) {
      buscarDocumentoPendiente();
    } else {
      setMensaje('Datos incompletos. Por favor inicia el proceso desde el inicio.');
      setCargandoDocumento(false);
    }
  }, [cedula, location.state]);

  const buscarDocumentoPendiente = async () => {
    try {
      // ⭐ Incluir firmante si existe
      let url = `${API_BASE_URL}/api/documento-pendiente/${cedula}`;
      if (firmanteIndex !== null && firmanteIndex !== undefined) {
        url += `?firmante=${firmanteIndex}`;
      }
      const response = await axios.get(url);
      if (response.data && response.data._id) {
        setDocumentId(response.data._id);
      } else {
        setMensaje('No se encontró un documento pendiente.');
        setCargandoDocumento(false);
      }
    } catch (error) {
      console.error('Error al buscar documento:', error);
      setMensaje('Error al buscar el documento pendiente.');
      setCargandoDocumento(false);
    }
  };

  // Cargar documento cuando tenemos el ID
  useEffect(() => {
    if (documentId && cedula) {
      cargarDocumento();
    }
  }, [documentId, cedula]);

  const cargarDocumento = async () => {
    if (!cedula || !documentId) return;

    setCargandoDocumento(true);
    setErrorCarga(false);
    setMensaje('');

    try {
      console.log('🔍 Cargando documento:', documentId);
      
      // ⭐ CORREGIDO: usar documentId (no documentoId) y agregar firmante
      let urlSeguro = `${API_BASE_URL}/api/documento-seguro/${documentId}?cedula=${cedula}`;
      if (firmanteIndex !== null && firmanteIndex !== undefined) {
        urlSeguro += `&firmante=${firmanteIndex}`;
      }
      const response = await axios.get(urlSeguro);
      const documento = response.data.documento;

      if (!documento || !documento.documento) {
        setMensaje('No se encontró un documento válido.');
        setCargandoDocumento(false);
        return;
      }

      const nombreArchivo = documento.documento.split(/[/\\]/).pop();
      const urlDocumento = `${API_BASE_URL}/uploads/${nombreArchivo}`;
      
      console.log('📄 URL del documento:', urlDocumento);

      // Intentar diferentes visores
      await configurarVisor(urlDocumento);
      setDocumentoDB(documento);
      setCargandoDocumento(false);
      
    } catch (error) {
      console.error('Error al obtener el documento:', error);
      setErrorCarga(true);
      
      if (reintentos < maxReintentos) {
        setMensaje(`Error al cargar. Reintentando... (${reintentos + 1}/${maxReintentos})`);
        setTimeout(() => {
          setReintentos(prev => prev + 1);
          cargarDocumento();
        }, 2000);
      } else {
        setMensaje('No tienes autorización para ver este documento.');
        setCargandoDocumento(false);
      }
    }
  };

  const configurarVisor = async (urlDocumento) => {
    // Verificar si el PDF es accesible
    try {
      await fetch(urlDocumento, { method: 'HEAD' });
      console.log('✅ PDF accesible');
    } catch (error) {
      console.log('❌ PDF no accesible directamente');
      throw error;
    }

    // Configurar visor con fallbacks
    const visores = [
      {
        tipo: 'google',
        url: `https://docs.google.com/gview?url=${encodeURIComponent(urlDocumento)}&embedded=true`,
        nombre: 'Google Docs Viewer'
      },
      {
        tipo: 'mozilla',
        url: `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(urlDocumento)}`,
        nombre: 'Mozilla PDF.js'
      },
      {
        tipo: 'directo',
        url: urlDocumento,
        nombre: 'Visor directo del navegador'
      }
    ];

    // Usar Google Docs por defecto, pero preparar fallbacks
    const visorPrimario = visores[0];
    setTipoVisor(visorPrimario.tipo);
    setDocumentUrl(visorPrimario.url);
    
    console.log(`🖥️ Usando ${visorPrimario.nombre}`);

    // Precargar otros visores en segundo plano
    setTimeout(() => {
      visores.slice(1).forEach(visor => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = visor.url;
        document.head.appendChild(link);
      });
    }, 1000);
  };

  const cambiarVisor = (nuevoTipo) => {
    if (!documentoDB) return;
    
    const nombreArchivo = documentoDB.documento.split(/[/\\]/).pop();
    const urlDocumento = `${API_BASE_URL}/uploads/${nombreArchivo}`;
    
    let nuevaUrl = '';
    switch (nuevoTipo) {
      case 'mozilla':
        nuevaUrl = `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(urlDocumento)}`;
        break;
      case 'directo':
        nuevaUrl = urlDocumento;
        break;
      default: // google
        nuevaUrl = `https://docs.google.com/gview?url=${encodeURIComponent(urlDocumento)}&embedded=true`;
    }
    
    setTipoVisor(nuevoTipo);
    setDocumentUrl(nuevaUrl);
    setVisorCargado(false);
    console.log(`🔄 Cambiando a visor: ${nuevoTipo}`);
  };

  const handleIframeLoad = () => {
    setVisorCargado(true);
    console.log('✅ Visor cargado exitosamente');
  };

  const handleIframeError = () => {
    console.log('❌ Error cargando visor');
    if (tipoVisor === 'google') {
      cambiarVisor('mozilla');
    } else if (tipoVisor === 'mozilla') {
      cambiarVisor('directo');
    }
  };

  // MODIFICACIÓN PRINCIPAL: Cambiar la navegación después de aceptar
  const handleAceptar = async () => {
    try {
      console.log('✅ Botón ACEPTAR presionado');
      console.log('📋 Cédula:', cedula);
      console.log('📄 Documento:', documentoDB?._id);
      console.log('👤 FirmanteIndex:', firmanteIndex);
      
      // Verificar que tenemos los datos necesarios
      if (!documentoDB || !cedula) {
        alert('⚠️ Error: Faltan datos. Por favor recarga la página (F5) e intenta de nuevo.');
        console.error('❌ Datos faltantes - documentoDB:', documentoDB, 'cedula:', cedula);
        return;
      }

      console.log('🚀 Iniciando navegación a selfie...');
      
      // Intentar navegar
      navigate(`/selfie-capture/${cedula}`, { 
        state: { 
          cedula, 
          documentoId: documentoDB._id,
          firmanteIndex: firmanteIndex  
        },
        replace: false
      });
      
      // Esperar un momento para ver si funcionó
      setTimeout(() => {
        console.log('⏱️ Navegación completada (esperamos)');
      }, 1000);
      
    } catch (error) {
      console.error('💥 ERROR en handleAceptar:', error);
      alert('⚠️ Hubo un error al continuar. Detalle: ' + error.message);
      
      // Intento de respaldo: usar window.location
      console.log('🔄 Intentando método alternativo...');
      const firmanteParam = firmanteIndex !== null && firmanteIndex !== undefined ? `?firmante=${firmanteIndex}` : '';
      window.location.href = `/selfie-capture/${cedula}${firmanteParam}`;
    }
  };

  const handleRechazar = async () => {
    if (procesandoRechazo) return;
    
    setProcesandoRechazo(true);
    
    try {
      const userAgent = navigator.userAgent;
      const dispositivo = /Mobile|Android|iPhone|iPad/.test(userAgent) ? 'Móvil' : 'Escritorio';
      const navegador = userAgent.includes('Chrome') ? 'Chrome' : 
                       userAgent.includes('Firefox') ? 'Firefox' : 
                       userAgent.includes('Safari') ? 'Safari' : 'Otro';

      let geo = { lat: 'N/A', lon: 'N/A' };
      try {
        if (navigator.geolocation) {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          geo = {
            lat: position.coords.latitude.toFixed(6),
            lon: position.coords.longitude.toFixed(6)
          };
        }
      } catch (geoError) {
        console.log('No se pudo obtener la geolocalización:', geoError);
      }

      let ip = 'No disponible';
      try {
        const ipResponse = await axios.get('https://api.ipify.org?format=json');
        ip = ipResponse.data.ip;
      } catch (ipError) {
        console.log('No se pudo obtener la IP:', ipError);
      }

      const response = await axios.put(`${API_BASE_URL}/api/rechazar-documento/${documentoDB._id}`, {
        geo, ip, dispositivo, navegador
      });

      if (response.data.success) {
        setMostrarModal(true);
      } else {
        alert('Ocurrió un error al rechazar el documento.');
      }
    } catch (error) {
      console.error('Error al rechazar el documento:', error);
      alert('Ocurrió un error al rechazar el documento.');
    } finally {
      setProcesandoRechazo(false);
    }
  };

  const handleCerrarModal = () => {
    setMostrarModal(false);
    window.location.href = 'https://colombianadetrasplantes.com';
  };

  const recargarDocumento = () => {
    setReintentos(0);
    setErrorCarga(false);
    cargarDocumento();
  };

  return (
    <div className="container mt-4">
      {/* Header con logos */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src="/logo3.png" alt="Logo 1" style={{ height: '60px' }} />
        <h2 className="text-center mb-0">Visualización del Documento</h2>
        <img src="/logo2.png" alt="Logo 2" style={{ height: '60px' }} />
      </div>

      {/* Mensajes de estado */}
      {mensaje && (
        <div className={`alert ${errorCarga ? 'alert-danger' : 'alert-info'} text-center`}>
          <strong>{mensaje}</strong>
          {errorCarga && (
            <div className="mt-2">
              <button className="btn btn-sm btn-outline-primary" onClick={recargarDocumento}>
                🔄 Intentar nuevamente
              </button>
            </div>
          )}
        </div>
      )}

      {/* Loading spinner */}
      {cargandoDocumento && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando documento...</p>
        </div>
      )}

      {/* Selector de visor y documento */}
      {documentUrl && !cargandoDocumento && (
        <>
          {/* Controles del visor */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted">Visor de documentos:</small>
                <div className="btn-group ms-2" role="group">
                  <button
                    className={`btn btn-sm ${tipoVisor === 'google' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => cambiarVisor('google')}
                  >
                    Google
                  </button>
                  <button
                    className={`btn btn-sm ${tipoVisor === 'mozilla' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => cambiarVisor('mozilla')}
                  >
                    Mozilla
                  </button>
                  <button
                    className={`btn btn-sm ${tipoVisor === 'directo' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => cambiarVisor('directo')}
                  >
                    Directo
                  </button>
                </div>
              </div>
              
              {!visorCargado && (
                <div className="text-muted">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Cargando vista previa...
                </div>
              )}
            </div>
          </div>

          {/* Contenedor del documento */}
          <div 
            className="position-relative mb-4"
            style={{
              paddingTop: '141.42%',
              height: 0,
              overflow: 'hidden',
              border: '2px solid #dee2e6',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa'
            }}
          >
            {!visorCargado && (
              <div 
                className="position-absolute top-50 start-50 translate-middle text-center"
                style={{ zIndex: 1 }}
              >
                <div className="spinner-border text-primary mb-2" role="status"></div>
                <p className="text-muted">Cargando vista previa del documento...</p>
              </div>
            )}
            
            <iframe
              src={documentUrl}
              title="Documento"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                opacity: visorCargado ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              allowFullScreen
            />
          </div>

          {/* Información de ayuda */}
          <div className="text-center mb-4">
            <div className="alert alert-info">
              <strong>ℹ️ ¿Qué deseas hacer?</strong>
              <div className="mt-2">
                <small>
                  • Si <strong>aceptas</strong> el documento, tomarás una selfie y luego firmarás.
                  <br />
                  • Si <strong>rechazas</strong> el documento, se generará un certificado de rechazo.
                </small>
              </div>
            </div>
          </div>

          {/* Botones flotantes grandes para adultos mayores */}
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '15px',
            zIndex: 1000,
            flexWrap: 'wrap',
            justifyContent: 'center',
            padding: '0 10px',
            maxWidth: '100%'
          }}>
            <button
              onClick={handleAceptar}
              disabled={procesandoRechazo}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                padding: '18px 35px',
                fontSize: '20px',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.4)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
                minWidth: '180px',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.6)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(40, 167, 69, 0.4)';
              }}
            >
              <span style={{ fontSize: '24px' }}>✓</span>
              Aceptar
            </button>

            <button
              onClick={handleRechazar}
              disabled={procesandoRechazo}
              style={{
                backgroundColor: procesandoRechazo ? '#6c757d' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                padding: '18px 35px',
                fontSize: '20px',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(220, 53, 69, 0.4)',
                cursor: procesandoRechazo ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
                minWidth: '180px',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => {
                if (!procesandoRechazo) {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 6px 20px rgba(220, 53, 69, 0.6)';
                }
              }}
              onMouseOut={(e) => {
                if (!procesandoRechazo) {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.4)';
                }
              }}
            >
              {procesandoRechazo ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid white',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Procesando...
                </>
              ) : (
                <>
                  <span style={{ fontSize: '24px' }}>✗</span>
                  Rechazar
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* Estilos para animaciones */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Ajustes responsivos para botones flotantes */
        @media (max-width: 768px) {
          .floating-buttons {
            flex-direction: column;
            width: 90%;
          }
        }
      `}</style>

      {/* Modal de confirmación de rechazo */}
      <Modal show={mostrarModal} onHide={handleCerrarModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>✓ Documento Rechazado</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <div className="mb-3">
            <div style={{ fontSize: '48px', color: '#dc3545' }}>🚫</div>
          </div>
          <p>
            El documento ha sido rechazado exitosamente. 
            Si desea solicitarlo nuevamente, por favor comuníquese con el funcionario que se lo envió.
          </p>
          <p className="text-muted">
            Agradecemos su atención.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="primary" onClick={handleCerrarModal} size="lg">
            Entendido
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default DocumentViewPage;